import { useState } from "react";
import {
    Dialog,
    DialogContent,
    TextField,
    Button,
    Stack,
    Typography,
    Alert,
    CircularProgress,
    Divider,
    Link,
    IconButton,
    InputAdornment,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "../auth/FirebaseContext";
import Logo from "./Logo";

export default function LoginDialog({ open, onClose, onSwitchToRegister, onSwitchToReset }) {
    const { t } = useTranslation();
    const { login, loginWithGoogle } = useAuthContext();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            onClose();
        } catch (err) {
            setError(t("auth.loginErrorShort"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xs"
            fullWidth
            PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
        >
            <DialogContent>
                <Stack alignItems="center" sx={{ mb: 3 }}>
                    <Logo size={48} />
                    <Typography variant="h5" sx={{ fontWeight: 700, mt: 1.5 }}>
                        {t("auth.loginTitle")}
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                        {t("auth.loginSubtitle")}
                    </Typography>
                </Stack>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Button
                    fullWidth
                    variant="outlined"
                    onClick={async () => {
                        setError("");
                        try {
                            await loginWithGoogle();
                            onClose();
                        } catch (err) {
                            if (err.code !== "auth/popup-closed-by-user") {
                                setError(t("auth.googleError", { message: err.message || t("auth.tryAgain") }));
                            }
                        }
                    }}
                    startIcon={<Icon icon="flat-color-icons:google" width={22} />}
                    sx={{
                        mb: 2,
                        py: 1.2,
                        borderRadius: 2,
                        borderColor: "grey.300",
                        color: "text.primary",
                        fontWeight: 600,
                        textTransform: "none",
                        fontSize: "0.95rem",
                        "&:hover": { borderColor: "grey.400", bgcolor: "grey.50" },
                    }}
                >
                    {t("auth.googleContinue")}
                </Button>

                <Divider sx={{ my: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        {t("auth.orWithEmail")}
                    </Typography>
                </Divider>

                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <TextField
                            fullWidth
                            label={t("auth.email")}
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon icon="mdi:email-outline" width={20} color="#919EAB" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            fullWidth
                            label={t("auth.password")}
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon icon="mdi:lock-outline" width={20} color="#919EAB" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            size="small"
                                            aria-label={showPassword ? t("common.hidePassword") : t("common.showPassword")}
                                        >
                                            <Icon icon={showPassword ? "mdi:eye-off" : "mdi:eye"} width={20} />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <Stack direction="row" justifyContent="flex-end">
                            <Link
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={onSwitchToReset}
                                sx={{ fontWeight: 600 }}
                            >
                                {t("auth.forgotPassword")}
                            </Link>
                        </Stack>
                        <Button
                            fullWidth
                            variant="contained"
                            type="submit"
                            size="large"
                            disabled={loading}
                            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                            sx={{ py: 1.3, borderRadius: 2, fontWeight: 700 }}
                        >
                            {loading ? t("auth.signingIn") : t("auth.signIn")}
                        </Button>
                    </Stack>
                </form>

                <Typography sx={{ mt: 2.5, textAlign: "center" }} variant="body2">
                    {t("auth.noAccount")}{" "}
                    <Link component="button" onClick={onSwitchToRegister} sx={{ fontWeight: 700 }}>
                        {t("auth.registerCta")}
                    </Link>
                </Typography>
            </DialogContent>
        </Dialog>
    );
}
